import { HttpClient } from '@angular/common/http';
import { Injectable, inject, computed, signal } from '@angular/core';
import {
  injectQuery,
  injectMutation,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { environment } from '../../../../../environments/environment';
import { lastValueFrom, take } from 'rxjs';
import type { DailyFactHero } from '../../../../shared/models/daily-fact-hero.model';

export type VideoStatus = 'PENDING' | 'SUCCESS' | 'FAILED' | null | undefined;

export type { DailyFactHero };

export interface DailyFact {
  id: string;
  date: string;
  fact: string;
  category: string;
  language: string;
  source?: string;
  sourceUrl?: string;
  viewCount?: number;
  isActive?: boolean;
  rewardXp?: number;
  videoStatus?: VideoStatus;
  videoUrl?: string; // Signed URL for video playback (when videoStatus=SUCCESS)
  videoPlaybackRate?: number; // Use at playback to undo stretch. 1 = no adjustment.
  hero?: DailyFactHero;
}

export interface ListDailyFactsResponse {
  facts: DailyFact[];
  totalCount: number;
}

export interface GenerateDailyFactVideoRequest {
  factId: string;
  fact: string;
  category: string;
  language: string;
  date: string;
}

const DAILY_FACTS_QUERY_KEY = ['dailyFacts'] as const;
const REFETCH_INTERVAL_MS = 10_000;
const DEFAULT_PAGE_SIZE = 10;

@Injectable({ providedIn: 'root' })
export class DailyFactsService {
  #http = inject(HttpClient);
  #queryClient = inject(QueryClient);

  hasPendingVideoStatus = signal(false);
  page = signal(0);
  pageSize = signal(DEFAULT_PAGE_SIZE);

  #dailyFactsQuery = injectQuery(() => {
    const limit = this.pageSize();
    const offset = this.page() * limit;
    return {
      queryKey: [...DAILY_FACTS_QUERY_KEY, this.page(), limit],
      retry: 1,
      queryFn: () =>
        lastValueFrom(
          this.#http
            .get<ListDailyFactsResponse>(
              `${environment.baseUrl}/activities/daily-facts?limit=${limit}&offset=${offset}`,
            )
            .pipe(take(1)),
        ),
      select: (data: ListDailyFactsResponse) => {
        const facts = data.facts ?? [];
        this.hasPendingVideoStatus.set(
          facts.some((f) => f.videoStatus === 'PENDING'),
        );
        return { facts, totalCount: data.totalCount ?? 0 };
      },
      refetchInterval: (): number | false =>
        this.hasPendingVideoStatus() ? REFETCH_INTERVAL_MS : false,
    };
  });

  generateDailyFactVideoMutation = injectMutation(() => ({
    mutationFn: (request: GenerateDailyFactVideoRequest) =>
      lastValueFrom(
        this.#http.post<{ id: string; status: string }>(
          `${environment.baseUrl}/ai/generate-daily-fact-video`,
          request,
        ),
      ),
    onSuccess: () => {
      this.#queryClient.invalidateQueries({ queryKey: DAILY_FACTS_QUERY_KEY });
    },
  }));

  facts = computed(() => this.#dailyFactsQuery.data()?.facts ?? []);
  totalCount = computed(() => this.#dailyFactsQuery.data()?.totalCount ?? 0);
  isLoading = computed(() => this.#dailyFactsQuery.isLoading());
  isPending = computed(() => this.generateDailyFactVideoMutation.isPending());
  error = computed(() => this.#dailyFactsQuery.error());

  refetch(): void {
    this.#queryClient.invalidateQueries({ queryKey: DAILY_FACTS_QUERY_KEY });
  }

  setPage(page: number): void {
    this.page.set(page);
  }

  setPageSize(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
  }
}
